<template>
  <InputWrapper
    v-slot="{ inputId }"
    class="select-action-dropdown"
    :input-id="id"
    :label="t('common.action')"
  >
    <Select
      v-model="selectedDropdownValue"
      :input-id="inputId"
      :options="resolvedOptions"
      :loading="loading"
      :disabled="!canTenderToCarrier"
      option-group-label="label"
      option-group-children="items"
      :placeholder="t('common.action')"
    >
      <template #value="{ value }">
        {{
          value.value === defaultTenderMethod
            ? t('common.paramDefault', { param: value.label })
            : value.label
        }}
      </template>

      <template #option="{ option }">
        {{
          option.value === defaultTenderMethod
            ? t('common.paramDefault', { param: option.label })
            : option.label
        }}
      </template>
    </Select>
  </InputWrapper>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useUid } from '@rygen/rygen-ui/composables';
import { mapEnumToTranslatedDropdownValues } from '@rygen/rygen-ui/i18n';
import {
  DropdownOption,
  TenderConfig,
  TenderMethod,
} from '@rygen/rygen-ui/model';

import { WellKnownRateStrategy } from '@/model';
import { useCreditUsageStore } from '@/stores/CreditUsage';

import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Select from 'primevue/select';

export interface GroupDropdownOption<T> {
  label: string;
  code: string;
  items: DropdownOption<T>[];
}

const selectedDropdownValue = defineModel({
  type: Object as PropType<DropdownOption<string>>,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('tender-action-group-dropdown').uid,
  },
  tenderConfig: {
    type: Array as PropType<TenderConfig[]>,
    default: () => [],
  },
  defaultTenderMethod: {
    type: String as PropType<TenderMethod>,
    default: () => TenderMethod.MANUAL,
  },
  rateStrategyType: {
    type: String,
    default: () => '',
  },
  isTenderOnly: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  tenderConfig,
  defaultTenderMethod,
  rateStrategyType,
  isTenderOnly,
  loading,
} = toRefs(props);

const { t } = useI18n();
const { canTenderToCarrier } = useCreditUsageStore();

const tenderMethodsOptions = mapEnumToTranslatedDropdownValues(
  TenderMethod,
  'TenderMethod',
);

const groupOptions = computed((): GroupDropdownOption<string>[] => {
  const groupDropdownOptions: GroupDropdownOption<string>[] = [];

  if (!tenderConfig.value.length) {
    // show tender method MANUAL, when none are configured
    const lonelyChildManualTenderOption = tenderMethodsOptions.filter(
      (method) => method.value === TenderMethod.MANUAL,
    );

    groupDropdownOptions.push({
      label: t('shipments.tenderMethods', 1),
      code: 'tender',
      items: lonelyChildManualTenderOption,
    });
  } else {
    const configuredMethods = carrierSettingConfiguredTenderMethods.value;
    const groupChildrenTenderOptions = tenderMethodsOptions.filter((method) =>
      configuredMethods?.includes(method.value as TenderMethod),
    );

    groupDropdownOptions.push({
      label: t('shipments.tenderMethods', groupChildrenTenderOptions.length),
      code: 'tender',
      items: groupChildrenTenderOptions,
    });
  }

  if (!isTenderOnly.value) {
    groupDropdownOptions.push({
      label: t('common.other'),
      code: 'assign',
      items: [
        {
          label: t('rateShop.assignAndTenderModal.assignWithoutTendering'),
          value: 'ASSIGN',
        },
      ],
    });
  }

  return groupDropdownOptions;
});

const resolvedOptions = computed((): GroupDropdownOption<string>[] => {
  if (canTenderToCarrier.value) {
    return groupOptions.value;
  }
  return groupOptions.value.filter((option) => option.code === 'assign');
});

const carrierSettingConfiguredTenderMethods = computed((): TenderMethod[] =>
  tenderConfig.value
    .map((config) => config.method)
    .filter((tenderMethod: TenderMethod) => {
      const isNonP44TenderMethod =
        tenderMethod !== TenderMethod.P44 &&
        tenderMethod !== TenderMethod.P44_VOLUME_LTL;

      const isP44 =
        tenderMethod === TenderMethod.P44 &&
        rateStrategyType.value === WellKnownRateStrategy.P44;

      const isP44Volume =
        tenderMethod === TenderMethod.P44_VOLUME_LTL &&
        rateStrategyType.value === WellKnownRateStrategy.P44_VOLUME;

      return isNonP44TenderMethod || isP44 || isP44Volume;
    }),
);
</script>
